import { USER_ROLES, ROLE_PERMISSIONS } from '../constants';

/**
 * Authentication utility functions for use throughout the application
 */

/**
 * Check if a user has a specific permission
 * @param {Object} user - User object
 * @param {string} permission - Permission to check
 * @returns {boolean} - Whether user has permission
 */
export const userHasPermission = (user, permission) => {
  if (!user || !user.role) return false;

  const permissions = ROLE_PERMISSIONS[user.role] || [];
  return permissions.includes(permission);
};

/**
 * Check if a user has any of the specified permissions
 * @param {Object} user - User object
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean} - Whether user has any of the permissions
 */
export const userHasAnyPermission = (user, permissions) => {
  if (!user || !user.role || !Array.isArray(permissions)) return false;

  const userPermissions = ROLE_PERMISSIONS[user.role] || [];
  return permissions.some(permission => userPermissions.includes(permission));
};

/**
 * Check if a user has all of the specified permissions
 * @param {Object} user - User object
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean} - Whether user has all permissions
 */
export const userHasAllPermissions = (user, permissions) => {
  if (!user || !user.role || !Array.isArray(permissions)) return false;

  const userPermissions = ROLE_PERMISSIONS[user.role] || [];
  return permissions.every(permission => userPermissions.includes(permission));
};

/**
 * Check if a user has a specific role
 * @param {Object} user - User object
 * @param {string} role - Role to check
 * @returns {boolean} - Whether user has role
 */
export const userHasRole = (user, role) => {
  return user?.role === role;
};

/**
 * Check if a user has a specific API role category
 * @param {Object} user - User object with roles structure from API
 * @param {string} roleKey - Role category key (e.g., 'admin', 'operation', 'marketing')
 * @param {string} specificRole - Optional specific role within the category
 * @returns {boolean} - Whether user has the API role
 */
export const userHasApiRole = (user, roleKey, specificRole = null) => {
  if (!user || !user.roles || typeof user.roles !== 'object') return false;

  const userRoles = user.roles;
  if (!userRoles[roleKey] || !Array.isArray(userRoles[roleKey])) return false;

  // If specific role is provided, check for it; otherwise just check if role key exists
  if (specificRole) {
    return userRoles[roleKey].includes(specificRole);
  }

  return userRoles[roleKey].length > 0;
};

/**
 * Get all roles for a user from API structure
 * @param {Object} user - User object with roles structure from API
 * @returns {string[]} - Array of role strings in format "category.role"
 */
export const getUserApiRoles = user => {
  if (!user || !user.roles || typeof user.roles !== 'object') return [];

  const allRoles = [];
  Object.keys(user.roles).forEach(roleKey => {
    const rolesInCategory = user.roles[roleKey];
    if (Array.isArray(rolesInCategory)) {
      rolesInCategory.forEach(role => {
        allRoles.push(`${roleKey}.${role}`);
      });
    }
  });

  return allRoles;
};

/**
 * Check if a user has any of the specified roles
 * @param {Object} user - User object
 * @param {string[]} roles - Array of roles to check
 * @returns {boolean} - Whether user has any of the roles
 */
export const userHasAnyRole = (user, roles) => {
  if (!user || !user.role || !Array.isArray(roles)) return false;
  return roles.includes(user.role);
};

/**
 * Check if a user is an admin (backward compatible)
 * @param {Object} user - User object
 * @returns {boolean} - Whether user is admin
 */
export const isAdmin = user => {
  return user?.role === USER_ROLES.ADMIN;
};

/**
 * Check if a user is an operation manager (backward compatible)
 * @param {Object} user - User object
 * @returns {boolean} - Whether user is operation manager
 */
export const isOperationManager = user => {
  return user?.role === USER_ROLES.OPERATION;
};

/**
 * Check if a user is a marketing manager (backward compatible)
 * @param {Object} user - User object
 * @returns {boolean} - Whether user is marketing manager
 */
export const isMarketingManager = user => {
  return user?.role === USER_ROLES.MARKETING;
};

/**
 * Check if a user has admin role using API structure
 * @param {Object} user - User object with API roles structure
 * @returns {boolean} - Whether user has admin role
 */
export const isApiAdmin = user => {
  return userHasApiRole(user, 'admin');
};

/**
 * Check if a user has operation role using API structure
 * @param {Object} user - User object with API roles structure
 * @returns {boolean} - Whether user has operation role
 */
export const isApiOperation = user => {
  return userHasApiRole(user, 'operation');
};

/**
 * Check if a user has marketing role using API structure
 * @param {Object} user - User object with API roles structure
 * @returns {boolean} - Whether user has marketing role
 */
export const isApiMarketing = user => {
  return userHasApiRole(user, 'marketing');
};

/**
 * Get all permissions for a user's role
 * @param {Object} user - User object
 * @returns {string[]} - Array of permissions
 */
export const getUserPermissions = user => {
  if (!user || !user.role) return [];
  return ROLE_PERMISSIONS[user.role] || [];
};

/**
 * Format user display name
 * @param {Object} user - User object
 * @returns {string} - Formatted display name
 */
export const formatUserDisplayName = user => {
  if (!user) return 'Unknown User';

  if (user.name) return user.name;
  if (user.firstName && user.lastName)
    return `${user.firstName} ${user.lastName}`;
  if (user.firstName) return user.firstName;
  if (user.email) return user.email;

  return 'Unknown User';
};

/**
 * Get user initials for avatar display
 * @param {Object} user - User object
 * @returns {string} - User initials
 */
export const getUserInitials = user => {
  if (!user) return 'U';

  const name = formatUserDisplayName(user);
  const parts = name.split(' ');

  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }

  return name.substring(0, 2).toUpperCase();
};

/**
 * Check if token is expired using API tokenExpired field
 * @param {Object} user - User object with tokenExpired field
 * @returns {boolean} - Whether token is expired
 */
export const isTokenExpiredFromApi = user => {
  if (!user || !user.tokenExpired) return true;

  try {
    const tokenExpiry = new Date(user.tokenExpired).getTime();
    const now = Date.now();
    return tokenExpiry <= now;
  } catch (error) {
    console.warn('Error parsing tokenExpired:', error);
    return true;
  }
};

/**
 * Check if token is expired (basic JWT check - backward compatible)
 * @param {string} token - JWT token
 * @returns {boolean} - Whether token is expired
 */
export const isTokenExpired = token => {
  if (!token) return true;

  try {
    // Basic JWT expiration check (for demo purposes)
    // In a real app, you'd properly decode and check the JWT
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    return true;
  }
};

/**
 * Get time until token expires using API tokenExpired field
 * @param {Object} user - User object with tokenExpired field
 * @returns {number} - Time in milliseconds until expiration
 */
export const getTokenTimeToExpiryFromApi = user => {
  if (!user || !user.tokenExpired) return 0;

  try {
    const tokenExpiry = new Date(user.tokenExpired).getTime();
    const now = Date.now();
    return Math.max(0, tokenExpiry - now);
  } catch (error) {
    console.warn('Error calculating token expiry time:', error);
    return 0;
  }
};

/**
 * Get time until token expires (JWT check - backward compatible)
 * @param {string} token - JWT token
 * @returns {number} - Time in milliseconds until expiration
 */
export const getTokenTimeToExpiry = token => {
  if (!token) return 0;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    const timeToExpiry = (payload.exp - currentTime) * 1000;
    return Math.max(0, timeToExpiry);
  } catch (error) {
    return 0;
  }
};

/**
 * Format token expiry time for display
 * @param {Object} user - User object with tokenExpired field
 * @returns {string} - Formatted time string
 */
export const formatTokenExpiryTime = user => {
  const remainingTime = getTokenTimeToExpiryFromApi(user);
  if (remainingTime <= 0) return 'Expired';

  const minutes = Math.floor(remainingTime / 60000);
  const seconds = Math.floor((remainingTime % 60000) / 1000);

  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
};

/**
 * Session storage helpers
 */
export const sessionUtils = {
  /**
   * Check if session should show timeout warning
   * @param {number} lastActivity - Last activity timestamp
   * @param {number} warningThreshold - Warning threshold in milliseconds
   * @returns {boolean} - Whether to show warning
   */
  shouldShowTimeoutWarning: (lastActivity, warningThreshold = 60000) => {
    const now = Date.now();
    const timeSinceActivity = now - lastActivity;
    return timeSinceActivity >= warningThreshold;
  },

  /**
   * Check if session is expired
   * @param {number} lastActivity - Last activity timestamp
   * @param {number} timeoutDuration - Session timeout duration in milliseconds
   * @returns {boolean} - Whether session is expired
   */
  isSessionExpired: (lastActivity, timeoutDuration) => {
    const now = Date.now();
    const timeSinceActivity = now - lastActivity;
    return timeSinceActivity >= timeoutDuration;
  },

  /**
   * Get remaining session time
   * @param {number} lastActivity - Last activity timestamp
   * @param {number} timeoutDuration - Session timeout duration in milliseconds
   * @returns {number} - Remaining time in milliseconds
   */
  getRemainingSessionTime: (lastActivity, timeoutDuration) => {
    const now = Date.now();
    const timeSinceActivity = now - lastActivity;
    return Math.max(0, timeoutDuration - timeSinceActivity);
  },

  /**
   * Format remaining time for display
   * @param {number} remainingTime - Remaining time in milliseconds
   * @returns {string} - Formatted time string
   */
  formatRemainingTime: remainingTime => {
    const minutes = Math.floor(remainingTime / 60000);
    const seconds = Math.floor((remainingTime % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  },
};

/**
 * Higher-order component helpers
 */
export const authHOC = {
  /**
   * Create a component that only renders if user has required permission
   * @param {string} permission - Required permission
   * @returns {Function} - HOC function
   */
  withPermission: permission => WrappedComponent => {
    return function PermissionGuardedComponent(props) {
      const { user } = props;

      if (!userHasPermission(user, permission)) {
        return null;
      }

      return <WrappedComponent {...props} />;
    };
  },

  /**
   * Create a component that only renders if user has required role
   * @param {string} role - Required role
   * @returns {Function} - HOC function
   */
  withRole: role => WrappedComponent => {
    return function RoleGuardedComponent(props) {
      const { user } = props;

      if (!userHasRole(user, role)) {
        return null;
      }

      return <WrappedComponent {...props} />;
    };
  },
};

export default {
  // Backward compatible functions
  userHasPermission,
  userHasAnyPermission,
  userHasAllPermissions,
  userHasRole,
  userHasAnyRole,
  isAdmin,
  isOperationManager,
  isMarketingManager,
  getUserPermissions,
  formatUserDisplayName,
  getUserInitials,
  isTokenExpired,
  getTokenTimeToExpiry,

  // New API-compatible functions
  userHasApiRole,
  getUserApiRoles,
  isApiAdmin,
  isApiOperation,
  isApiMarketing,
  isTokenExpiredFromApi,
  getTokenTimeToExpiryFromApi,
  formatTokenExpiryTime,

  // Utilities
  sessionUtils,
  authHOC,
};
